// Libraries
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { isNil, isNotNil } from 'ramda';

// MUI
import { GridSize } from '@mui/material';

// Core
import { ResourceTextApplication } from '../../../../core/resources';

// Common
import { ADialog, ADialogTitle } from '../../../../common/dialog';

import ConfirmDialogClose from '../../../../common/dialog/ConfirmDialogClose';

// Interfaces
import { AnnouncementTypeEnum, DialogModeEnum } from '../../../../interfaces/enums';
import { IAnimalAnnouncement, IUpdateAnnouncementResult } from '../../../../interfaces/IAnnouncement';
import {
    IAnimalSummary,
    IAnimalSummaryExtraInfo,
    IFarmAnimal,
} from '../../../../interfaces/IAnimal';
import { ITiltuApiResult } from '../../../../interfaces/IGeneral';

// Store
// API
// Feature
import CloseDialogButton from '../general/CloseDialogButton';
import AnimalAnnouncement from '../AnimalAnnouncement';
import '../../../shared/styles/sharedStyles.scss';
import { GUIDType } from '../../../../interfaces/types';

export interface IAnnouncementDialogProps {
    data: IAnimalAnnouncement | null;
    onClose: (openPurchaseForConfirmation?: boolean) => void;
    mode: DialogModeEnum;
    selectedAnimals: IAnimalSummary[] | null;
    selectedHoldingSites?: string[];
    animals: IAnimalSummaryExtraInfo[] | null;
    processAnnouncementSave: (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string
    ) => void;
    processAnnouncementDelete: (announcementId: GUIDType) => void;
    mapUpdatedSalesTypeToAllAnimalsData: (data: IFarmAnimal[]) => void;
    loading: boolean;
    canCreateAnimalAnnouncement: boolean | null;
    readOnly?: boolean;
    chainInformationFound?: boolean;
    dataImportedViaMmfi?: boolean;
    announcementConfirmation?: boolean;
}

export interface IGridSize {
    Xs: GridSize;
    Sm: GridSize;
    Md: GridSize;
    Lg: GridSize;
}

export default function BovineSlaughterAnnouncement(props: IAnnouncementDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaBovine', 'AnelmaGeneral']);

    const [confirmationDialogClose, setConfirmationDialogClose] = useState<boolean>(false);

    const handleDialogClose = () => {
        if (props.readOnly === true) {
            props.onClose();
        } else {
            setConfirmationDialogClose(true);
        }
    };

    const getTitleResource = () => {
        if (props.mode === DialogModeEnum.Create) {
            return t('AnelmaBovine:1020');
        } else if (!props.announcementConfirmation && props.mode === DialogModeEnum.Edit) {
            return t('AnelmaBovine:1044');
        } else if (
            isNotNil(props.announcementConfirmation) &&
            props.announcementConfirmation &&
            props.mode === DialogModeEnum.Edit
        ) {
            return t('AnelmaBovine:1192');
        }
    };

    const getAnnouncementType = () => {
        // Only slaughter announcements can be created via front-end, preliminary announcements are created automatically
        if (props.mode === DialogModeEnum.Create) {
            return AnnouncementTypeEnum.Slaughter;
        } else if (props.mode === DialogModeEnum.Edit && isNotNil(props.data)) {
            return props.data.Type;
        }
        return AnnouncementTypeEnum.Slaughter;
    };

    return (
        <ADialog open onClose={props.onClose} disableEnforceFocus>
            <ADialogTitle>
                {getTitleResource()}
                <CloseDialogButton
                    className='animal-announcement-close-button-float-right'
                    onClick={handleDialogClose}
                />
            </ADialogTitle>
            <AnimalAnnouncement
                data-robot-id='animal-slaughter-announcement'
                announcementType={getAnnouncementType()}
                handleDialogClose={handleDialogClose}
                {...props}
            />
            {confirmationDialogClose && (
                <ConfirmDialogClose
                    data-robot-id='animal-slaughter-confirm-close'
                    confirmation='AnelmaGeneral:1065'
                    onCancel={() => setConfirmationDialogClose(false)}
                    onClose={() => props.onClose()}
                    open={confirmationDialogClose}
                />
            )}
        </ADialog>
    );
}
